import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, FormControl, Checkbox } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { TextField } from '@material-ui/core';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { makeURL } from '../../../helperFunctions';
import useViewportWidth from '../../../myHooks/useViewportWidth';

const useStyles = makeStyles(theme => ({
  mainContainer: ({ isInGroup, isMulti }) => ({
    width: '100%',
    maxWidth: isMulti ? '600px' : '650px',
    margin: '50px auto 30px auto',
    gap: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: isInGroup ? '1px solid lightgray' : 'none',
    paddingBottom: isInGroup ? '25px' : 'none',
    flexWrap: 'wrap',
    '@media (max-width: 768px)': {
      width: '100% !important',
      maxWidth: '100%',
    },
  }),
  text: ({ isSummary }) => ({
    color: theme.palette.text.primary,
    fontWeight: 800,
    fontSize: '18px',
    margin: '0',
    textAlign: 'left',
    '@media (max-width: 600px)': {
      fontSize: '16px',
    },
  }),
  dropdownWrapper: {
    '@media (max-width: 768px)': {
      width: '100%',
    },
  },
  dropdown: {
    padding: '10px 13px',
    '@media (max-width: 768px)': {
      width: '100%',
    },
  },
  autocompleteDropdown: {
    paddingBottom: '0px',
  },
  checkboxRoot: {
    color: '#ccc',
    '&.Mui-checked': {
      color: '#19989B',
    },
  },
  renderTagsText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },
  multiSelectInputRoot: {
    maxHeight: '80px !important',
    paddingBottom: '10px !important',
    paddingLeft: '10px !important',
  },
}));

const ClientsDropdownAutocomplete = props => {
  const history = useHistory();
  const appData = useSelector(({ data }) => data);

  // For social survey we display a regular dropdown, brand/buyer is multiselect
  const isMulti = props.id !== 'Social_Clients_Select';

  useEffect(() => {
    if (!appData.visibleAgencies.length) {
      history.replace(makeURL('/clients/all'));
    }
  }, [appData.visibleAgencies.length, history]);

  if (!appData.visibleAgencies.length) return null;

  const dropdownOptions = appData.ownedAgencyStats
    .filter(e => appData.visibleAgencies.includes(e.agencyId))
    .map(client => ({
      text: client.name,
      value: client.agencyId,
    }));

  const initialValue = isMulti ? [dropdownOptions[0].value] : dropdownOptions[0].value;
  const [selectedValue, setSelectedValue] = useState(initialValue);

  useEffect(() => {
    if (!props.value) {
      props.handleSurveyAnswers(selectedValue, props.id);
    }
  }, []);

  const handleAutoCompleteChange = (event, newValue) => {
    const newSelected = isMulti ? newValue.map(v => v.value) : newValue.value;
    if (isMulti && !newSelected.length) return;
    setSelectedValue(newSelected);
    props.handleSurveyAnswers(newSelected, props.id);
    if (!isMulti) {
      props.handleSurveyAnswers(undefined, 'Post_Brand_Persona');
    }
  };

  if (props.hasTextWrapper) {
    return (
      <AutocompleteWrapper text={props.text} isMulti={isMulti} isInGroup={props.isInGroup}>
        <AutocompleteInner
          dropdownOptions={dropdownOptions}
          onChange={handleAutoCompleteChange}
          selectedValue={selectedValue}
          value={props.value}
          isSummary={props.isSummary}
          isMulti={isMulti}
        />
      </AutocompleteWrapper>
    );
  } else {
    return (
      <AutocompleteInner
        dropdownOptions={dropdownOptions}
        onChange={handleAutoCompleteChange}
        selectedValue={selectedValue}
        value={props.value}
        isSummary={props.isSummary}
        isMulti={isMulti}
      />
    );
  }
};

const AutocompleteWrapper = props => {
  const classes = useStyles({ isInGroup: props.isInGroup, isMulti: props.isMulti });
  return (
    <Box className={classes.mainContainer}>
      <Typography component="div" variant="h3" className={classes.text}>
        {props.text}
      </Typography>
      {props.children}
    </Box>
  );
};

const AutocompleteInner = props => {
  const [open, setOpen] = useState(false);
  const classes = useStyles({ isSummary: props.isSummary });
  const width = useViewportWidth();
  const isBigScreen = width > 400;

  let currentValue;
  if (props.isMulti) {
    currentValue = props.value
      ? props.dropdownOptions.filter(opt => props.value.includes(opt.value))
      : props.dropdownOptions.filter(opt => props.selectedValue.includes(opt.value));
  } else {
    currentValue = props.value
      ? props.dropdownOptions.find(e => e.value === props.value)
      : props.dropdownOptions.find(e => e.value === props.selectedValue);
  }

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  return (
    <Box
      className={classes.dropdownWrapper}
      style={{
        minWidth: props.isSummary && isBigScreen ? '360px' : '300px',
        maxWidth: props.isSummary && isBigScreen ? '360px' : '300px',
      }}>
      <FormControl fullWidth>
        {props.isMulti ? (
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={props.dropdownOptions}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            getOptionLabel={option => option.text}
            getOptionSelected={(option, value) => option.value === value.value}
            onChange={props.onChange}
            value={currentValue}
            disableClearable
            classes={{ inputRoot: classes.multiSelectInputRoot }}
            renderOption={(option, { selected }) => (
              <>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  checked={selected}
                  classes={{ root: classes.checkboxRoot }}
                />
                {option.text}
              </>
            )}
            // 👇 replaces chips with a single text line and ellipsis
            renderTags={selected => (
              <span className={classes.renderTagsText}>{selected.map(opt => opt.text).join(', ')}</span>
            )}
            renderInput={params => (
              <TextField
                {...params}
                onClick={() => setOpen(true)}
                variant="outlined"
                size="small"
                className={classes.autocompleteDropdown}
              />
            )}
          />
        ) : (
          <Autocomplete
            options={props.dropdownOptions}
            getOptionLabel={option => option.text}
            getOptionSelected={(option, value) => option.value === value.value}
            onChange={props.onChange}
            disableClearable
            disabled={props.isSummary && !props.isMulti}
            value={currentValue}
            renderInput={params => (
              <TextField {...params} variant="outlined" size="small" className={classes.autocompleteDropdown} />
            )}
          />
        )}
      </FormControl>
    </Box>
  );
};

export default ClientsDropdownAutocomplete;
